import * as React from 'react';

import {
  deriveFurthestStep,
  type SetupLinearStep,
  type SetupMilestones,
} from '@/components/setup/experience/lib/setupSteps';
import { buildSetupProductsListInput } from '@/components/setup/experience/lib/setupCatalogSummary';
import type { SetupSessionStore, SetupSourcePath } from '@/components/setup/experience/types';
import { useIntegrationsList } from '@/lib/api/integrations';
import { useProductsList } from '@/lib/api/products';
import { useShippingRatesList } from '@/lib/api/shippingRates';
import { useFunnelsList } from '@/lib/api/funnels';
import { useStore } from '@/lib/api/stores';

// storeId-scoped query — we only need to know whether any shipping rate exists. The select mirrors
// the list endpoint's DTO so the returned rows parse cleanly (a narrower select would drop required
// fields); `limit: 1` keeps it cheap.
const SHIPPING_EXISTS_QUERY = (storeId: string) => ({
  drizzQuery: {
    select: ['id', 'storeId', 'shippingRateName', 'icon', 'amount', 'countryCodes', 'isFree', 'createdAt'],
    from: 'shippingRates',
    where: [{ table: 'shippingRates', column: 'storeId', operator: 'eq' as const, value: storeId }],
    orderBy: [{ table: 'shippingRates', column: 'createdAt', direction: 'desc' as const }],
    limit: 1,
    offset: 0,
    joins: [],
  },
});

function isPlatformProvider(provider: string): provider is SetupSourcePath {
  return provider === 'shopify' || provider === 'woocommerce';
}

export interface SetupProgress {
  isLoading: boolean;
  /** storeId is in the URL but no such store exists server-side (e.g. deleted) → recovery. */
  storeMissing: boolean;
  store: SetupSessionStore | null;
  milestones: SetupMilestones;
  furthestStep: SetupLinearStep;
  /** Digital stores skip the shipping step — drives the conditional flow shape. */
  isDigital: boolean;
  /** Provider of the connected platform integration, if any (drives the checkout/source view). */
  connectedProvider: SetupSourcePath | null;
  funnelId: string | null;
}

/**
 * Derives the merchant's setup progress entirely from the database for a given store. There is no
 * persisted session: how far they got is whatever the DB says exists (store → products/integration
 * → shipping → payment → funnel). Steps still fetch their own detail; this only computes the gate.
 */
export function useSetupProgress(storeId: string | null): SetupProgress {
  const enabled = Boolean(storeId);
  const id = storeId ?? '';

  const storeQuery = useStore(id, enabled);
  const integrationsQuery = useIntegrationsList(id);
  const productsQuery = useProductsList(buildSetupProductsListInput(id), { enabled });
  const shippingQuery = useShippingRatesList(SHIPPING_EXISTS_QUERY(id), { enabled });
  const funnelsQuery = useFunnelsList(id);

  const store = React.useMemo<SetupSessionStore | null>(() => {
    const data = storeQuery.data;
    if (!data) return null;
    return {
      id: data.id,
      name: data.name,
      baseCurrency: data.baseCurrency,
      primaryColor: data.config?.primaryColor ?? '',
      logoUrl: data.config?.logoUrl ?? '',
      digitalProduct: data.digitalProduct ?? false,
      vertical: data.vertical ?? null,
    };
  }, [storeQuery.data]);

  const isDigital = store?.digitalProduct ?? false;

  const connectedIntegration = integrationsQuery.data?.find(
    (integration) => integration.enabled && isPlatformProvider(integration.provider),
  );

  const milestones: SetupMilestones = {
    hasStore: Boolean(store),
    hasProducts: (productsQuery.data?.items?.length ?? 0) > 0,
    hasIntegration: Boolean(connectedIntegration),
    hasShipping: (shippingQuery.data?.items?.length ?? 0) > 0,
    hasPayment: Boolean(storeQuery.data?.selectedPaymentFlowId),
    hasFunnel: (funnelsQuery.data?.funnels?.length ?? 0) > 0,
  };

  const funnels = funnelsQuery.data?.funnels ?? [];
  const funnelId = funnels.find((funnel) => funnel.isDefault)?.id ?? funnels[0]?.id ?? null;

  return {
    isLoading:
      enabled &&
      (storeQuery.isLoading ||
        integrationsQuery.isLoading ||
        productsQuery.isLoading ||
        shippingQuery.isLoading ||
        funnelsQuery.isLoading),
    storeMissing: enabled && !storeQuery.isLoading && !storeQuery.data,
    store,
    milestones,
    furthestStep: deriveFurthestStep(milestones, isDigital),
    isDigital,
    connectedProvider: connectedIntegration ? (connectedIntegration.provider as SetupSourcePath) : null,
    funnelId,
  };
}
